import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Calendar, ChevronLeft, ChevronRight } from "react-feather";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formattedYMDDate } from "../../../../utils/dateUtils";
import { Alert, Button, Card, CardHeader, CardBody } from "reactstrap";
import SearchableSelect from "../../../../CommonElements/SearchableSelect";
import SkeletonLoader from "../../../../CommonElements/Loader/SkeletonLoader";
import { useEmployeeProfile } from "../../../../hooks/useEmployeeProfile";
import { useGetAttendanceLeaveSummaryQuery } from "../../../../ReduxToolkit/api/attendanceSummary";
import { useListAttendanceQuery } from "../../../../ReduxToolkit/api/attendance";
import { toAttendanceSummary } from "../../../attendance/utils/attendanceSummary";
import { attendanceCycle } from "../../../../utils/attendanceCycle";
import AttendanceSummaryPopup from "../../../dashboard/components/AttendanceSummaryPopup";
import { IProfileLeaveAttendancePopup } from "../../../../Types/UserProfileDetails";
import { canViewEmployeeAttendance } from "../../utils/attendanceAccess";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const EmployeeAttendanceSummaryTab = ({
  selfService = false,
  onRangeChange,
  onSummaryChange,
  renderDayAction,
  renderDayTime,
}: {
  selfService?: boolean;
  onRangeChange?: (range: { startDate: string; endDate: string }) => void;
  onSummaryChange?: (summary: IProfileLeaveAttendancePopup) => void;
  renderDayAction?: (date: string) => ReactNode;
  renderDayTime?: (
    date: string,
    kind: "in_time" | "out_time",
    time: string | null,
  ) => ReactNode;
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [lastSummary, setLastSummary] = useState<{
    employeeId: string;
    summary: IProfileLeaveAttendancePopup;
  }>();
  const { id, employeeProfileDetails, loggedInEmployee, loggedInEmployeeRole } = useEmployeeProfile();
  const allowed =
    ["admin", "hr"].includes(loggedInEmployeeRole) ||
    (selfService && canViewEmployeeAttendance(employeeProfileDetails, loggedInEmployee)) ||
    (selfService && String(id) === String(loggedInEmployee?.id));
  const [selection, setSelection] = useState(() => {
    const today = new Date();
    const cycleEnd = new Date(
      today.getFullYear(),
      today.getMonth() - (!selfService && today.getDate() < 25 ? 1 : 0),
      25,
    );
    return { year: cycleEnd.getFullYear(), month: cycleEnd.getMonth() + 1 };
  });
  const [customRange, setCustomRange] = useState<
    { startDate: string; endDate: string } | undefined
  >(() => {
    if (!selfService) return undefined;
    const initial = attendanceCycle(selection.year, selection.month);
    const today = new Date();
    const startDate =
      today.getDate() > 25
        ? formattedYMDDate(new Date(today.getFullYear(), today.getMonth(), 26))
        : initial.startDate;
    return { startDate, endDate: formattedYMDDate(today) };
  });
  const [draftRange, setDraftRange] = useState<[Date | null, Date | null]>(
    () => {
      const initial =
        customRange ?? attendanceCycle(selection.year, selection.month);
      return [
        new Date(`${initial.startDate}T00:00:00`),
        new Date(`${initial.endDate}T00:00:00`),
      ];
    },
  );
  const range =
    selfService && customRange
      ? customRange
      : attendanceCycle(selection.year, selection.month);
  useEffect(() => {
    onRangeChange?.({ startDate: range.startDate, endDate: range.endDate });
  }, [onRangeChange, range.startDate, range.endDate]);
  const summaryQuery = useGetAttendanceLeaveSummaryQuery(
    { userId: String(id ?? ""), ...range },
    { skip: selfService || !allowed || !id },
  );
  const endYear = Number(range.endDate.slice(0, 4));
  const attendanceQuery = useListAttendanceQuery(
    {
      userId: String(id ?? ""),
      startDate: range.startDate,
      endDate: range.endDate,
      activeYear: endYear - (Number(range.endDate.slice(5, 7)) < 10 ? 1 : 0),
    },
    { skip: !selfService || !allowed || !id },
  );
  const adaptedResponse = useMemo(
    () =>
      attendanceQuery.currentData
        ? {
            success: attendanceQuery.currentData.success,
            message: attendanceQuery.currentData.message,
            data: toAttendanceSummary(attendanceQuery.currentData, range),
          }
        : undefined,
    [attendanceQuery.currentData, range.startDate, range.endDate],
  );
  const response = selfService ? adaptedResponse : summaryQuery.currentData;
  const { isFetching, isError, refetch } = selfService
    ? attendanceQuery
    : summaryQuery;
  const body = response?.data;
  const receivedSummary =
    body &&
    ("leave_attendance_popup" in body ? body.leave_attendance_popup : body);
  const failed =
    isError ||
    (!selfService && summaryQuery.currentData?.status === false) ||
    response?.success === false;
  useEffect(() => {
    if (receivedSummary && !failed && !isFetching)
      onSummaryChange?.(receivedSummary);
  }, [receivedSummary, failed, isFetching, onSummaryChange]);
  useEffect(() => {
    if (receivedSummary && !failed && !isFetching) {
      setLastSummary({
        employeeId: String(id),
        summary: {
          ...receivedSummary,
          cycle_start: range.startDate,
          cycle_end: range.endDate,
        },
      });
    }
  }, [receivedSummary, failed, isFetching, id, range.startDate, range.endDate]);
  if (!allowed) return null;
  const previousSummary =
    lastSummary?.employeeId === String(id) ? lastSummary.summary : undefined;
  const summary =
    receivedSummary && !failed && !isFetching
      ? {
          ...receivedSummary,
          cycle_start: range.startDate,
          cycle_end: range.endDate,
        }
      : isFetching || failed
        ? previousSummary
        : undefined;
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 11 }, (_, i) => currentYear - i);
  const changeCycle = (next: typeof selection) => {
    setSelection(next);
    window.requestAnimationFrame(() => {
      headingRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      headingRef.current?.focus({ preventScroll: true });
    });
  };
  const shiftCycle = (offset: number) => {
    const date = new Date(selection.year, selection.month - 1 + offset, 1);
    changeCycle({ year: date.getFullYear(), month: date.getMonth() + 1 });
  };
  const cycleOptions = months.map((name, i) => ({
    value: i + 1,
    label: `${months[(i + 11) % 12].slice(0, 3)} to ${name.slice(0, 3)}`,
    description: `26 ${months[(i + 11) % 12].slice(0, 3)} – 25 ${name.slice(0, 3)}`,
  }));
  const cycleTitle = (value: string) => {
    const [year, month, day] = value.split("-");
    return day + " " + months[Number(month) - 1].slice(0, 3) + " " + year;
  };
  return (
    <Card className="attendance-summary-popup attendance-summary-profile">
      <CardHeader
        className={`attendance-cycle-header ${selfService ? "attendance-range-header" : "b-l-primary border-3"} py-2 px-3 d-flex flex-wrap justify-content-between align-items-center gap-2`}
      >
        <h5
          ref={headingRef}
          tabIndex={-1}
          className="attendance-cycle-title mb-0 f-w-400 f-14"
        >
          {selfService ? "Attendance" : "Attendance Summary"} (
          {cycleTitle(summary?.cycle_start ?? range.startDate)} –{" "}
          {cycleTitle(summary?.cycle_end ?? range.endDate)})
        </h5>
        {selfService ? (
          <div className="attendance-range-picker">
            <label className="visually-hidden" htmlFor="attendance-date-range">
              Attendance date range
            </label>
            <Calendar size={15} aria-hidden="true" />
            <ReactDatePicker
              id="attendance-date-range"
              selectsRange
              maxDate={new Date()}
              startDate={draftRange[0]}
              endDate={draftRange[1]}
              dateFormat="dd/MM/yyyy"
              className="form-control form-control-sm"
              popperPlacement="bottom-end"
              placeholderText="Select start and end date"
              onChange={(dates: [Date | null, Date | null]) => {
                setDraftRange(dates);
                if (dates[0] && dates[1]) {
                  setCustomRange({
                    startDate: formattedYMDDate(dates[0]),
                    endDate: formattedYMDDate(dates[1]),
                  });
                }
              }}
            />
          </div>
        ) : (
          <div className="attendance-cycle-controls">
            <button
              type="button"
              className="attendance-cycle-arrow"
              aria-label="Previous attendance cycle"
              title="Previous cycle"
              disabled={
                selection.year === currentYear - 10 && selection.month === 1
              }
              onClick={() => shiftCycle(-1)}
            >
              <ChevronLeft size={16} />
            </button>
            <SearchableSelect
              className="attendance-cycle-month"
              name="attendance-cycle-month"
              label="Cycle"
              value={selection.month}
              options={cycleOptions}
              onChange={(value) =>
                changeCycle({ ...selection, month: Number(value) })
              }
            />
            <SearchableSelect
              className="attendance-cycle-year"
              name="attendance-cycle-year"
              label="Ending year"
              value={selection.year}
              options={years.map((year) => ({
                value: year,
                label: String(year),
              }))}
              onChange={(value) =>
                changeCycle({ ...selection, year: Number(value) })
              }
            />
            <button
              type="button"
              className="attendance-cycle-arrow"
              aria-label="Next attendance cycle"
              title="Next cycle"
              disabled={
                selection.year === currentYear && selection.month === 12
              }
              onClick={() => shiftCycle(1)}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </CardHeader>
      <div className="attendance-cycle-content" aria-busy={isFetching}>
        {isFetching && summary && (
          <div className="attendance-cycle-loading" role="status">
            Updating {cycleTitle(range.startDate)} – {cycleTitle(range.endDate)}
            …
          </div>
        )}
        {failed && (
          <Alert color="danger" className="m-3">
            {response?.message ?? "Unable to load attendance summary."}{" "}
            <Button color="link" onClick={() => refetch()}>
              Retry
            </Button>
          </Alert>
        )}
        {summary ? (
          <AttendanceSummaryPopup
            key={String(id)}
            employeeId={id}
            summaryData={summary}
            canViewTeamAttendance={selfService && canViewEmployeeAttendance(employeeProfileDetails, loggedInEmployee)}
            renderDayAction={renderDayAction}
            renderDayTime={renderDayTime}
            initialPageSize={selfService ? 50 : 6}
            canShow
            embedded
            hideHeader
          />
        ) : isFetching ? (
          <CardBody
            className="p-3"
            role="status"
            aria-label="Loading attendance summary"
          >
            <span className="visually-hidden">Loading attendance summary</span>
            <div className="attendance-summary-grid">
              {Array.from({ length: 7 }, (_, i) => (
                <div key={i}>
                  <SkeletonLoader className="w-100" height={54} />
                </div>
              ))}
            </div>
            <SkeletonLoader className="w-100 mb-2" height={40} />
            <SkeletonLoader className="w-100 mb-3" height={34} />
            <SkeletonLoader className="w-100 mb-2" height={28} />
            {Array.from({ length: 6 }, (_, i) => (
              <SkeletonLoader key={i} className="w-100 mb-1" height={30} />
            ))}
          </CardBody>
        ) : failed ? null : (
          <CardBody className="p-3">
            <Alert color="light">
              No attendance summary available for this cycle.
            </Alert>
          </CardBody>
        )}
      </div>
    </Card>
  );
};
export default EmployeeAttendanceSummaryTab;
